import { useEffect, useState } from "react";
import CrudPage from "@/components/CrudPage";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function DIDs() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const [ops, setOps] = useState([]);
  const [ipbxs, setIpbxs] = useState([]);
  const [anuncios, setAnuncios] = useState([]);
  const [ivrs, setIvrs] = useState([]);
  const [ramais, setRamais] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [filas, setFilas] = useState([]);
  const [timeConditions, setTimeConditions] = useState([]);
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    const unscoped = isSuperAdmin ? { skipTenantScope: true } : undefined;
    const requests = [
      api.get("/operadoras", unscoped),
      api.get("/ipbxs", unscoped),
      api.get("/anuncios", unscoped),
      api.get("/ivrs", unscoped),
      api.get("/ramais", unscoped),
      api.get("/grupos", unscoped),
      api.get("/filas", unscoped),
      api.get("/time-conditions", unscoped),
      ...(isSuperAdmin ? [api.get("/tenants", { skipTenantScope: true })] : []),
    ];
    Promise.all(requests)
      .then((results) => {
        const [o, i, a, v, r, g, f, tc, t] = results;
        setOps(o.data || []);
        setIpbxs(i.data || []);
        setAnuncios(a.data || []);
        setIvrs(v.data || []);
        setRamais(r.data || []);
        setGrupos(g.data || []);
        setFilas(f.data || []);
        setTimeConditions(tc.data || []);
        setTenants(t?.data || []);
      })
      .catch(() => {
        setOps([]);
        setIpbxs([]);
        setAnuncios([]);
        setIvrs([]);
        setRamais([]);
        setGrupos([]);
        setFilas([]);
        setTimeConditions([]);
        setTenants([]);
      });
  }, [isSuperAdmin]);

  const opMap = Object.fromEntries(ops.map((o) => [o.id, o.nome]));
  const ipbxMap = Object.fromEntries(ipbxs.map((i) => [i.id, i.nome]));
  const anuncioMap = Object.fromEntries(anuncios.map((a) => [a.id, a.nome]));
  const scopedMap = (rows) => Object.fromEntries(
    rows.map((row) => [`${row.tenant_id || ""}:${row.numero}`, row.nome])
  );
  const ivrMap = scopedMap(ivrs);
  const ramalMap = scopedMap(ramais);
  const grupoMap = scopedMap(grupos);
  const filaMap = scopedMap(filas);
  const tenantMap = Object.fromEntries(tenants.map((t) => [t.id, t.nome]));
  const belongsToTenant = (row, tenantId) => !tenantId || row.tenant_id === tenantId || row.universal === true;
  const scopedName = (map, row, number) => map[`${row.tenant_id || ""}:${number}`] || "";

  const destino = (row) => {
    if ((row.destino_tipo === "ura" || row.destino_tipo === "ivr") && row.destino_padrao) {
      const name = scopedName(ivrMap, row, row.destino_padrao);
      return (
        <span>
          URA / IVR: {row.destino_padrao}
          {name ? ` - ${name}` : ""}
        </span>
      );
    }
    if (row.destino_tipo === "ramal" && row.destino_padrao) {
      const name = scopedName(ramalMap, row, row.destino_padrao);
      return (
        <span>
          Ramal: {row.destino_padrao}
          {name ? ` - ${name}` : ""}
        </span>
      );
    }
    if (row.destino_tipo === "grupo" && row.destino_padrao) {
      const name = scopedName(grupoMap, row, row.destino_padrao);
      return (
        <span>
          Grupo: {row.destino_padrao}
          {name ? ` - ${name}` : ""}
        </span>
      );
    }
    if (row.destino_tipo === "fila" && row.destino_padrao) {
      const name = scopedName(filaMap, row, row.destino_padrao);
      return (
        <span>
          Fila: {row.destino_padrao}
          {name ? ` - ${name}` : ""}
        </span>
      );
    }
    if (row.ipbx_id) return <span>{ipbxMap[row.ipbx_id] || "IPBX"}</span>;
    if (row.anuncio_id) return <span>Anuncio: {anuncioMap[row.anuncio_id] || "-"}</span>;
    return <span className="text-[color:var(--text-muted)]">sem destino</span>;
  };

  return (
    <CrudPage
      title="DIDs"
      subtitle="Numeros autorizados e destino de entrada"
      endpoint="/dids"
      testidPrefix="dids"
      headers={[...(isSuperAdmin ? ["Cliente"] : []), "Numero E.164", "Operadora", "Destino", "Descricao", "Status"]}
      renderRow={(row) => (
        <>
          {isSuperAdmin ? <td>{tenantMap[row.tenant_id] || row.tenant_id || "-"}</td> : null}
          <td className="font-mono font-medium">{row.numero}</td>
          <td>{opMap[row.operadora_id] || <span className="text-[color:var(--text-muted)]">-</span>}</td>
          <td>{destino(row)}</td>
          <td className="text-[color:var(--text-secondary)]">{row.descricao || "-"}</td>
          <td>{row.enabled ? <span className="sbc-tag tag-online">ativo</span> : <span className="sbc-tag tag-offline">inativo</span>}</td>
        </>
      )}
      defaultValues={{
        tenant_id: user?.tenant_id || "",
        numero: "",
        operadora_id: "",
        destino_tipo: "sem_destino",
        destino_padrao: "",
        ipbx_id: "",
        anuncio_id: "",
        time_condition_id: "",
        destino_fora_tipo: "",
        destino_fora_padrao: "",
        ipbx_fora_id: "",
        anuncio_fora_id: "",
        descricao: "",
        enabled: true,
      }}
      fields={[
        ...(isSuperAdmin ? [{
          key: "tenant_id",
          label: "Cliente",
          type: "select",
          full: true,
          options: [
            { value: "", label: "-- selecione o cliente --" },
            ...tenants.map((tenant) => ({ value: tenant.id, label: tenant.nome })),
          ],
          onChange: (next, value) => ({
            ...next,
            tenant_id: value,
            operadora_id: "",
            ipbx_id: "",
            anuncio_id: "",
            destino_padrao: "",
            time_condition_id: "",
            destino_fora_tipo: "",
            destino_fora_padrao: "",
            ipbx_fora_id: "",
            anuncio_fora_id: "",
          }),
        }] : []),
        { key: "numero", label: "Numero DID E.164", mono: true, placeholder: "+557135999160", full: true },
        {
          key: "operadora_id",
          label: "Operadora",
          type: "select",
          options: (form) => [
            { value: "", label: "-- selecione --" },
            ...ops
              .filter((row) => belongsToTenant(row, isSuperAdmin ? form.tenant_id : user?.tenant_id))
              .map((o) => ({ value: o.id, label: o.nome })),
          ],
        },
        {
          key: "time_condition_id",
          label: "Condicao de tempo",
          type: "select",
          full: true,
          options: (form) => [
            { value: "", label: "Sem condicao - funcionamento 24 horas" },
            ...timeConditions
              .filter((row) => row.enabled !== false && belongsToTenant(row, isSuperAdmin ? form.tenant_id : user?.tenant_id))
              .map((row) => ({ value: row.id, label: row.nome })),
          ],
          hint: "Dentro do horario o DID segue o destino configurado. Fora do horario usa o destino Fora da condicao.",
        },
        {
          key: "destino_fora_tipo",
          label: "Destino fora da condicao",
          type: "select",
          hidden: (form) => !form.time_condition_id,
          options: [
            { value: "", label: "Usar destino fora da propria condicao" },
            { value: "hangup", label: "Desligar chamada" },
            { value: "ramal", label: "Ramal direto" },
            { value: "grupo", label: "Grupo de toque" },
            { value: "fila", label: "Fila" },
            { value: "ura", label: "URA / IVR" },
            { value: "anuncio", label: "Anuncio" },
          ],
        },
        {
          key: "destino_tipo",
          label: "Tipo de destino",
          type: "select",
          options: [
            { value: "sem_destino", label: "Sem destino" },
            { value: "ipbx", label: "IPBX externo" },
            { value: "anuncio", label: "Anuncio" },
            { value: "ramal", label: "Ramal direto" },
            { value: "grupo", label: "Grupo de toque" },
            { value: "fila", label: "Fila" },
            { value: "ura", label: "URA / IVR" },
          ],
        },
        {
          key: "ipbx_id",
          label: "Destino IPBX",
          type: "select",
          hidden: (form) => form.destino_tipo !== "ipbx",
          options: (form) => [
            { value: "", label: "Selecione o IPBX" },
            ...ipbxs
              .filter((row) => belongsToTenant(row, isSuperAdmin ? form.tenant_id : user?.tenant_id))
              .map((i) => ({ value: i.id, label: i.nome })),
          ],
        },
        {
          key: "anuncio_id",
          label: "Destino anuncio",
          type: "select",
          hidden: (form) => form.destino_tipo !== "anuncio",
          options: (form) => [
            { value: "", label: "Selecione o anuncio" },
            ...anuncios
              .filter((row) => belongsToTenant(row, isSuperAdmin ? form.tenant_id : user?.tenant_id))
              .map((a) => ({ value: a.id, label: a.nome })),
          ],
        },
        {
          key: "destino_padrao",
          label: "Destino interno",
          type: "select",
          hidden: (form) => !["ramal", "grupo", "fila", "ura", "ivr"].includes(form.destino_tipo),
          options: (form) => [
            {
              value: "",
              label:
                form.destino_tipo === "ramal"
                  ? "Selecione o ramal direto"
                  : form.destino_tipo === "grupo"
                    ? "Selecione o grupo"
                    : form.destino_tipo === "fila"
                      ? "Selecione a fila"
                      : "Selecione a URA / IVR",
            },
            ...(
              form.destino_tipo === "ramal"
                ? ramais
                    .filter((row) => belongsToTenant(row, isSuperAdmin ? form.tenant_id : user?.tenant_id))
                    .map((row) => ({ value: row.numero, label: `${row.numero} - ${row.nome}` }))
                : form.destino_tipo === "grupo"
                  ? grupos
                      .filter((row) => belongsToTenant(row, isSuperAdmin ? form.tenant_id : user?.tenant_id))
                      .map((row) => ({ value: row.numero, label: `${row.numero} - ${row.nome}` }))
                  : form.destino_tipo === "fila"
                    ? filas
                        .filter((row) => belongsToTenant(row, isSuperAdmin ? form.tenant_id : user?.tenant_id))
                        .map((row) => ({ value: row.numero, label: `${row.numero} - ${row.nome}` }))
                    : ivrs
                        .filter((row) => belongsToTenant(row, isSuperAdmin ? form.tenant_id : user?.tenant_id))
                        .map((row) => ({ value: row.numero, label: `${row.numero} - ${row.nome}` }))
            ),
          ],
        },
        {
          key: "anuncio_fora_id",
          label: "Anuncio fora do horario",
          type: "select",
          hidden: (form) => form.destino_fora_tipo !== "anuncio",
          options: (form) => [
            { value: "", label: "Selecione o anuncio" },
            ...anuncios
              .filter((row) => belongsToTenant(row, isSuperAdmin ? form.tenant_id : user?.tenant_id))
              .map((a) => ({ value: a.id, label: a.nome })),
          ],
        },
        {
          key: "destino_fora_padrao",
          label: "Destino fora do horario",
          type: "select",
          hidden: (form) => !["ramal", "grupo", "fila", "ura", "ivr"].includes(form.destino_fora_tipo),
          options: (form) => [
            {
              value: "",
              label:
                form.destino_fora_tipo === "ramal"
                  ? "Selecione o ramal direto"
                  : form.destino_fora_tipo === "grupo"
                    ? "Selecione o grupo"
                    : form.destino_fora_tipo === "fila"
                      ? "Selecione a fila"
                      : "Selecione a URA / IVR",
            },
            ...(
              form.destino_fora_tipo === "ramal"
                ? ramais
                    .filter((row) => belongsToTenant(row, isSuperAdmin ? form.tenant_id : user?.tenant_id))
                    .map((row) => ({ value: row.numero, label: `${row.numero} - ${row.nome}` }))
                : form.destino_fora_tipo === "grupo"
                  ? grupos
                      .filter((row) => belongsToTenant(row, isSuperAdmin ? form.tenant_id : user?.tenant_id))
                      .map((row) => ({ value: row.numero, label: `${row.numero} - ${row.nome}` }))
                  : form.destino_fora_tipo === "fila"
                    ? filas
                        .filter((row) => belongsToTenant(row, isSuperAdmin ? form.tenant_id : user?.tenant_id))
                        .map((row) => ({ value: row.numero, label: `${row.numero} - ${row.nome}` }))
                    : ivrs
                        .filter((row) => belongsToTenant(row, isSuperAdmin ? form.tenant_id : user?.tenant_id))
                        .map((row) => ({ value: row.numero, label: `${row.numero} - ${row.nome}` }))
            ),
          ],
          hint: "Se preencher aqui, este DID usa esse destino especificamente fora do horario, sem depender do destino padrao da condicao.",
        },
        { key: "descricao", label: "Descricao", placeholder: "Ex: Numero principal Salvador", full: true },
        { key: "enabled", label: "Ativo", type: "toggle" },
      ]}
    />
  );
}
